import { type ReactNode, useState } from "react";

/** 週ストリップの各日が最低限持つべきデータ。 */
export type WeekStripDay = {
  /** ISO 日付 (YYYY-MM-DD) */
  d: string;
  /** 曜日ラベル (例: 月) */
  dow: string;
  /** 日 (1-31) */
  dom: number;
  /** 当日かどうか */
  isToday: boolean;
};

/**
 * 1 週間分の日付を横並びで表示し、日付選択・前後週移動・今日移動を行う共通ストリップ。
 * 各日セル下部に出す情報は `renderFooter` で差し込む (注文/運転日報/配車で内容が異なるため)。
 */
export function WeekStrip<T extends WeekStripDay>({
  days,
  selected,
  onSelect,
  onPrev,
  onNext,
  onToday,
  renderFooter,
  title,
  onPrevMonth,
  onNextMonth,
  isDayDisabled,
}: {
  days: T[];
  /** 選択中の ISO 日付 */
  selected: string;
  onSelect: (d: string) => void;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  /** 各日セルの下部に描画する内容。`sel` は当該セルが選択中かどうか。 */
  renderFooter?: (day: T, sel: boolean) => ReactNode;
  /** ストリップ上部に表示する年月タイトル。指定時のみタイトルバーを描画。 */
  title?: ReactNode;
  /** 前月へ移動。`title` と併用。 */
  onPrevMonth?: () => void;
  /** 翌月へ移動。`title` と併用。 */
  onNextMonth?: () => void;
  /** true を返す日はクリック不可 (過去日無効化などに使う)。 */
  isDayDisabled?: (day: T) => boolean;
}) {
  const [hovered, setHovered] = useState<string | null>(null);
  const [todayHov, setTodayHov] = useState(false);
  const [prevHov, setPrevHov] = useState(false);
  const [nextHov, setNextHov] = useState(false);
  const [prevMonHov, setPrevMonHov] = useState(false);
  const [nextMonHov, setNextMonHov] = useState(false);
  return (
    <div>
      {title != null && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 4,
            background: "var(--bg-raised)",
            padding: "8px 12px 0",
          }}
        >
          {onPrevMonth && (
            <button
              type="button"
              onClick={onPrevMonth}
              onMouseEnter={() => setPrevMonHov(true)}
              onMouseLeave={() => setPrevMonHov(false)}
              aria-label="前月"
              style={{
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
                color: prevMonHov ? "var(--ink)" : "var(--ink-2)",
                background: prevMonHov ? "var(--bg-panel)" : "transparent",
                border: "0.5px solid var(--rule-soft)",
                borderRadius: 4,
                transition: "background 0.1s",
              }}
            >
              ‹
            </button>
          )}
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "var(--ink)",
              minWidth: 120,
              textAlign: "center",
            }}
          >
            {title}
          </span>
          {onNextMonth && (
            <button
              type="button"
              onClick={onNextMonth}
              onMouseEnter={() => setNextMonHov(true)}
              onMouseLeave={() => setNextMonHov(false)}
              aria-label="翌月"
              style={{
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
                color: nextMonHov ? "var(--ink)" : "var(--ink-2)",
                background: nextMonHov ? "var(--bg-panel)" : "transparent",
                border: "0.5px solid var(--rule-soft)",
                borderRadius: 4,
                transition: "background 0.1s",
              }}
            >
              ›
            </button>
          )}
        </div>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          background: "var(--bg-raised)",
          borderBottom: "0.5px solid var(--rule)",
          padding: "0 12px",
        }}
      >
        <button
          type="button"
          onClick={onToday}
          onMouseEnter={() => setTodayHov(true)}
          onMouseLeave={() => setTodayHov(false)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 16px",
            fontSize: 11,
            color: todayHov ? "var(--ink)" : "var(--ink-2)",
            fontWeight: 500,
            background: todayHov ? "var(--bg-panel)" : "transparent",
            borderRadius: 2,
            transition: "background 0.1s",
          }}
        >
          今日
        </button>
        <div style={{ width: 0.5, background: "var(--rule-softer)", margin: "8px 0" }} />
        <button
          type="button"
          onClick={onPrev}
          onMouseEnter={() => setPrevHov(true)}
          onMouseLeave={() => setPrevHov(false)}
          aria-label="前の週"
          style={{
            alignSelf: "stretch",
            margin: "6px 8px",
            padding: "0 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            color: prevHov ? "var(--ink)" : "var(--ink-2)",
            background: prevHov ? "var(--bg-panel)" : "transparent",
            border: "0.5px solid var(--rule-soft)",
            borderRadius: 4,
            transition: "background 0.1s",
          }}
        >
          ‹
        </button>
        <div style={{ display: "flex", flex: 1, gap: 4, padding: "6px 4px" }}>
          {days.map((w) => {
            const sel = w.d === selected;
            const disabled = isDayDisabled?.(w) ?? false;
            return (
              <button
                key={w.d}
                type="button"
                aria-current={sel ? "date" : undefined}
                disabled={disabled}
                onClick={() => onSelect(w.d)}
                onMouseEnter={() => !disabled && setHovered(w.d)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  flex: 1,
                  minWidth: 80,
                  padding: "6px 8px",
                  background: sel
                    ? "oklch(0.97 0.04 55)"
                    : hovered === w.d
                      ? "var(--bg-raised)"
                      : w.isToday
                        ? "var(--bg-panel)"
                        : "transparent",
                  color: "var(--ink)",
                  border: sel
                    ? "2px solid oklch(0.7 0.15 55)"
                    : hovered === w.d
                      ? "0.5px solid var(--rule)"
                      : "0.5px solid var(--rule-softer)",
                  textAlign: "left",
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  transition: "background 0.1s",
                  opacity: disabled ? 0.4 : 1,
                  cursor: disabled ? "not-allowed" : "pointer",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                  }}
                >
                  <span
                    style={{
                      fontSize: 10,
                      fontFamily: "var(--f-mono)",
                      color: sel ? "oklch(0.75 0 0)" : "var(--ink-3)",
                    }}
                  >
                    {w.dow}
                  </span>
                  {w.isToday && (
                    <span
                      style={{
                        fontSize: 9,
                        color: sel ? "oklch(0.7 0.1 30)" : "var(--accent)",
                        fontWeight: 600,
                      }}
                    >
                      本日
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontFamily: "var(--f-mono)",
                    fontWeight: 500,
                    lineHeight: 1,
                  }}
                >
                  {String(w.dom).padStart(2, "0")}
                </div>
                {renderFooter?.(w, sel)}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={onNext}
          onMouseEnter={() => setNextHov(true)}
          onMouseLeave={() => setNextHov(false)}
          aria-label="次の週"
          style={{
            alignSelf: "stretch",
            margin: "6px 8px",
            padding: "0 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            color: nextHov ? "var(--ink)" : "var(--ink-2)",
            background: nextHov ? "var(--bg-panel)" : "transparent",
            border: "0.5px solid var(--rule-soft)",
            borderRadius: 4,
            transition: "background 0.1s",
          }}
        >
          ›
        </button>
      </div>
    </div>
  );
}
